import { gql } from '@apollo/client';
import type { TypedDocumentNode } from '@apollo/client';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button/index.tsx';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons/index.tsx';
import { iconChevronLeft } from '@sorare/core/src/atoms/icons/system/iconChevronLeft.ts';
import {
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex/index.tsx';
import { SecondaryPageBackground } from '@sorare/core/src/atoms/ui/SecondaryPageBackground/index.tsx';
import { Dialog } from '@sorare/core/src/components/dialog/index.tsx';
import { SeriesBalance } from '@sorare/core/src/components/navigation/AppNavigation/UserCurrencies/SeriesBalance/index.tsx';
import { useRetainedValue } from '@sorare/core/src/hooks/useRetainedValue.ts';
import type { HexColor } from '@sorare/core/src/style/types.ts';

import { ReferralMissionDetails } from '../ReferralMissionDetails/index.tsx';
import type { ReferralMissionDialog_inviteTaskAsReferrer } from './__generated__/index.graphql.ts';

const Root = styled(Vertical).attrs({ gap: 1.5 })`
  position: relative;
  min-height: 100%;
  isolation: isolate;
`;
const HeaderWrapper = styled.div`
  width: 100%;
  position: sticky;
  top: 0;
`;

type Props = {
  open: boolean;
  onClose: () => void;
  task: ReferralMissionDialog_inviteTaskAsReferrer | null | undefined;
  surfacePrimary: HexColor;
  surfacePrimaryText: HexColor;
  surfaceSecondaryText: HexColor;
};

export const ReferralMissionDialog = ({
  open,
  onClose,
  task,
  surfacePrimary,
  surfacePrimaryText,
  surfaceSecondaryText,
}: Props) => {
  const retainedTask = useRetainedValue(task);

  return (
    <Dialog open={open} fullWidth maxWidth="xs" hideHeader>
      <Root>
        <SecondaryPageBackground
          overlayStartColor="var(--c-surface-light)"
          overlayMidColor="var(--c-competition-contenter-secondary)"
          overlayEndColor="var(--c-competition-mls-tertiary)"
        />
        <HeaderWrapper>
          <SBHorizontal className="p-1 pb-0">
            <Button
              size="medium"
              color="tertiary"
              startIcon={<FontAwesomeIcon icon={iconChevronLeft} />}
              onClick={onClose}
            />
            <SeriesBalance color="tertiary" />
          </SBHorizontal>
        </HeaderWrapper>
        {!!retainedTask && (
          <ReferralMissionDetails
            task={retainedTask}
            surfacePrimary={surfacePrimary}
            surfacePrimaryText={surfacePrimaryText}
            surfaceSecondaryText={surfaceSecondaryText}
          />
        )}
      </Root>
    </Dialog>
  );
};

ReferralMissionDialog.fragments = {
  inviteTaskAsReferrer: gql`
    fragment ReferralMissionDialog_inviteTaskAsReferrer on InviteTaskAsReferrer {
      id
      ...ReferralMissionDetails_inviteTaskAsReferrer
    }
    ${ReferralMissionDetails.fragments.inviteTaskAsReferrer}
  ` as TypedDocumentNode<ReferralMissionDialog_inviteTaskAsReferrer>,
};
